import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Mail, Phone, Globe, FileText, CheckCircle, AlertTriangle, Briefcase, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../utils/api';

const registerEmployerAPI = async (data) => {
  try {
    const response = await api.post('/auth/employer/register', data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Registration failed');
  }
};

const EmployerRegistration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    fullName: '',
    designation: '',
    branch: '',
    email: '',
    mobile: '',
    country: 'India',
    sourceType: 'SMAART_NETWORK',
    gstin: '',
    cin: '',
    identifier: '',
    termsAccepted: {
      hiringTerms: false,
      fairHiring: false,
      dataProcessing: false
    }
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('terms.')) {
      const termName = name.split('.')[1];
      setFormData(prev => ({ ...prev, termsAccepted: { ...prev.termsAccepted, [termName]: checked } }));
      return;
    }
    let sanitizedValue = value;
    if (name === 'mobile') sanitizedValue = value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, [name]: sanitizedValue });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const countryOptions = ['India', 'United Kingdom', 'United States', 'Canada', 'Australia', 'Singapore', 'United Arab Emirates', 'Japan'];
  const branchOptions = ['HR', 'Recruitment', 'Talent Acquisition', 'Management', 'Operations', 'Administration', 'Finance', 'Marketing', 'Sales', 'Engineering', 'Legal', 'Other'];

  const validate = () => {
    const newErrors = {};
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.fullName.trim()) newErrors.fullName = 'Contact person name is required';
    if (!formData.designation.trim()) newErrors.designation = 'Designation is required';
    if (!formData.branch) newErrors.branch = 'Branch is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Work email is required';
    } else if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (formData.mobile.length !== 10) {
      newErrors.mobile = 'Mobile must be 10 digits';
    }
    if (formData.country === 'India') {
      if (!formData.gstin.trim()) newErrors.gstin = 'GSTIN is required';
      if (!formData.cin.trim()) newErrors.cin = 'CIN Number is required';
    } else {
      if (!formData.identifier.trim()) newErrors.identifier = 'Legal identifier is required';
    }
    const { termsAccepted } = formData;
    if (!termsAccepted.hiringTerms || !termsAccepted.fairHiring || !termsAccepted.dataProcessing) {
      toast.error('You must accept all terms and conditions to proceed.');
      return false;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const result = await registerEmployerAPI(formData);
      setIsRegistered(true);
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (isRegistered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#00152E] via-[#0a2a5e] to-[#1a3884] p-4">
        <div className="max-w-md w-full bg-white rounded-3xl p-10 shadow-2xl text-center">
          <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-green-100">
            <CheckCircle className="w-12 h-12 text-green-500" strokeWidth={2} />
          </div>
          <h2 className="text-2xl font-black text-[#00152E] tracking-tight mb-3">Registration Submitted!</h2>
          <p className="text-gray-500 text-[15px] font-medium mb-8 leading-relaxed">
            Thank you for registering with SMAART. Our Admin team will verify your details and reach out to you shortly at <strong className="text-[#1a3884]">{formData.email}</strong>.
          </p>
          <div className="bg-blue-50 rounded-2xl p-4 mb-8 border border-blue-100">
            <p className="text-blue-700 text-sm font-semibold">📬 A confirmation email has been sent to your inbox.</p>
          </div>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full bg-[#1a3884] hover:bg-[#112455] text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg transition-all"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex font-sans" style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>

      {/* ── LEFT PANEL ── */}
      <div className="hidden lg:flex lg:w-[360px] xl:w-[440px] flex-col bg-[#00152E] p-10 xl:p-12 relative overflow-hidden shrink-0" style={{ minHeight: '100vh' }}>
        {/* Subtle enterprise decorative rings */}
        <div className="absolute top-8 right-8 w-28 h-28 rounded-full border border-[#1a3884] opacity-40 pointer-events-none" />
        <div className="absolute top-8 right-8 w-16 h-16 rounded-full border border-[#2a4fa0] opacity-30 pointer-events-none" />
        <div className="absolute bottom-10 left-6 w-20 h-20 rounded-full border border-[#1a3884] opacity-25 pointer-events-none" />

        {/* ── TOP: Logo ── */}
        <div className="relative z-10 flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center border border-white/15">
            <Briefcase className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-black text-lg tracking-tight">SMAART Institute</span>
        </div>

        {/* ── MIDDLE: Content (grows to fill space) ── */}
        <div className="relative z-10 flex-1 flex flex-col justify-center">
          <p className="text-[#6b8cda] text-[10px] font-bold uppercase tracking-[0.2em] mb-3">Employer Portal</p>
          <h1 className="text-3xl font-black text-white leading-snug mb-3">
            Connect with<br />
            <span className="text-[#7ca4f4]">Campus Talent</span>
          </h1>
          <p className="text-[#8ba7d8] text-sm leading-relaxed mb-8">
            Register your company and get verified access to skilled, job-ready graduates from partner institutions.
          </p>

          {/* How it works */}
          <div className="mb-7">
            <p className="text-[#6b8cda] text-[10px] font-bold uppercase tracking-[0.18em] mb-4">How It Works</p>
            <div className="space-y-4">
              {[
                { step: '01', title: 'Register Your Company', desc: 'Fill in your company details and legal identifiers.' },
                { step: '02', title: 'Admin Verification', desc: 'Our team reviews and verifies your registration.' },
                { step: '03', title: 'Access the Portal', desc: 'Post jobs, review profiles, and schedule drives.' },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-md bg-[#1a3884] border border-[#2a4fa0] flex items-center justify-center flex-shrink-0 text-[#7ca4f4] text-[10px] font-black">{step}</div>
                  <div className="pt-0.5">
                    <div className="text-white text-sm font-semibold leading-tight">{title}</div>
                    <div className="text-[#8ba7d8] text-xs font-medium mt-0.5">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What you get */}
          <div className="bg-[#0d2150] border border-[#1e3a75] rounded-xl p-4 space-y-2.5">
            <p className="text-[#6b8cda] text-[10px] font-bold uppercase tracking-[0.18em] mb-3">What You Get</p>
            {[
              'Access to verified student profiles',
              'Conduct placement drives & job fairs',
              'Dedicated HR contact & support',
              'Secure data handling (GDPR/DPDP)',
            ].map(item => (
              <div key={item} className="flex items-center gap-2.5">
                <CheckCircle className="w-3.5 h-3.5 text-[#7ca4f4] flex-shrink-0" />
                <span className="text-[#a8c0e8] text-xs font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── BOTTOM: Copyright ── */}
        <div className="relative z-10 pt-8 border-t border-[#1a3884]/50 mt-8">
          <p className="text-[#4a6899] text-[11px] font-medium text-center">
            © {new Date().getFullYear()} SMAART Institute. All rights reserved.
          </p>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="flex-1 flex flex-col justify-center bg-gray-50 overflow-y-auto">
        <div className="w-full max-w-xl mx-auto px-6 py-10">

          {/* Mobile header */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-12 h-12 bg-[#1a3884] rounded-xl flex items-center justify-center mx-auto mb-3">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-black text-[#00152E]">SMAART Institute</h1>
            <p className="text-gray-500 text-sm mt-1">Employer Onboarding Portal</p>
          </div>

          {/* Form header */}
          <div className="mb-8">
            <h2 className="text-2xl font-black text-[#00152E] tracking-tight">Become a SMAART Employer Partner</h2>
            <p className="text-gray-500 text-sm font-medium mt-2">Complete the form below. Your account will be verified by our Admin team before activation.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">

            {/* Company & Name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Company Name *</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text" name="companyName" value={formData.companyName} onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-2.5 border ${errors.companyName ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'} rounded-xl focus:ring-2 focus:ring-[#1a3884]/30 focus:border-[#1a3884] text-sm transition-all`}
                    placeholder="TechNova Pvt Ltd"
                  />
                </div>
                {errors.companyName && <p className="mt-1 text-xs text-red-500">{errors.companyName}</p>}
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Full Name *</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text" name="fullName" value={formData.fullName} onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-2.5 border ${errors.fullName ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'} rounded-xl focus:ring-2 focus:ring-[#1a3884]/30 focus:border-[#1a3884] text-sm transition-all`}
                    placeholder="Vikram Shah"
                  />
                </div>
                {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName}</p>}
              </div>
            </div>

            {/* Designation & Branch */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Designation *</label>
                <input
                  type="text" name="designation" value={formData.designation} onChange={handleChange}
                  className={`w-full px-4 py-2.5 border ${errors.designation ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'} rounded-xl focus:ring-2 focus:ring-[#1a3884]/30 focus:border-[#1a3884] text-sm transition-all`}
                  placeholder="e.g. HR Manager"
                />
                {errors.designation && <p className="mt-1 text-xs text-red-500">{errors.designation}</p>}
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Branch *</label>
                <select
                  name="branch" value={formData.branch} onChange={handleChange}
                  className={`w-full px-4 py-2.5 border ${errors.branch ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'} rounded-xl focus:ring-2 focus:ring-[#1a3884]/30 focus:border-[#1a3884] text-sm transition-all ${formData.branch ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  <option value="" disabled>Select branch...</option>
                  {branchOptions.map(b => (
                    <option key={b} value={b} className="text-gray-900">{b}</option>
                  ))}
                </select>
                {errors.branch && <p className="mt-1 text-xs text-red-500">{errors.branch}</p>}
              </div>
            </div>

            {/* Email & Mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Work Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email" name="email" value={formData.email} onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-2.5 border ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'} rounded-xl focus:ring-2 focus:ring-[#1a3884]/30 focus:border-[#1a3884] text-sm transition-all`}
                    placeholder="name@company.com"
                  />
                </div>
                {errors.email ? (
                  <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                ) : formData.email && (formData.email.includes('@gmail.com') || formData.email.includes('@yahoo.com')) ? (
                  <p className="mt-1 text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Free domains may delay verification.</p>
                ) : null}
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Mobile Number *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel" name="mobile" value={formData.mobile} onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-2.5 border ${errors.mobile ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'} rounded-xl focus:ring-2 focus:ring-[#1a3884]/30 focus:border-[#1a3884] text-sm transition-all`}
                    placeholder="10-digit number" maxLength={10}
                  />
                </div>
                {errors.mobile && <p className="mt-1 text-xs text-red-500">{errors.mobile}</p>}
              </div>
            </div>

            {/* Country */}
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">Country of Registration *</label>
              <div className="relative">
                <select
                  name="country" value={formData.country} onChange={handleChange}
                  className="w-full pl-4 pr-10 py-2.5 border border-gray-200 bg-white rounded-xl focus:ring-2 focus:ring-[#1a3884]/30 focus:border-[#1a3884] text-sm transition-all"
                >
                  {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* India: GSTIN + CIN | Others: identifier */}
            {formData.country === 'India' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">GSTIN Number *</label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text" name="gstin" value={formData.gstin} onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-2.5 border ${errors.gstin ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'} rounded-xl focus:ring-2 focus:ring-[#1a3884]/30 focus:border-[#1a3884] text-sm transition-all`}
                      placeholder="Enter 15-digit GSTIN" maxLength={15}
                    />
                  </div>
                  {errors.gstin && <p className="mt-1 text-xs text-red-500">{errors.gstin}</p>}
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">CIN Number *</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text" name="cin" value={formData.cin} onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-2.5 border ${errors.cin ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'} rounded-xl focus:ring-2 focus:ring-[#1a3884]/30 focus:border-[#1a3884] text-sm transition-all`}
                      placeholder="Enter 21-digit CIN" maxLength={21}
                    />
                  </div>
                  {errors.cin && <p className="mt-1 text-xs text-red-500">{errors.cin}</p>}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                  {formData.country === 'United Kingdom' ? 'Companies House Number *' :
                    formData.country === 'United States' ? 'Employer Identification Number (EIN) *' :
                      formData.country === 'Canada' ? 'Business Number (BN) *' :
                        formData.country === 'Australia' ? 'Australian Business Number (ABN) *' :
                          formData.country === 'Singapore' ? 'Unique Entity Number (UEN) *' :
                            formData.country === 'Japan' ? 'Corporate Number (13 digits) *' :
                              'Company Registration Number *'}
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text" name="identifier" value={formData.identifier} onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-2.5 border ${errors.identifier ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'} rounded-xl focus:ring-2 focus:ring-[#1a3884]/30 focus:border-[#1a3884] text-sm transition-all`}
                    placeholder="Enter registration number"
                  />
                </div>
                {errors.identifier && <p className="mt-1 text-xs text-red-500">{errors.identifier}</p>}
              </div>
            )}

            {/* Terms */}
            <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
              <h4 className="text-xs font-black text-gray-700 uppercase tracking-wider">Required Legal Agreements</h4>
              {[
                { name: 'terms.hiringTerms', checked: formData.termsAccepted.hiringTerms, label: 'General Hiring Terms', desc: 'I agree to the SMAART platform terms of service for employers.' },
                { name: 'terms.fairHiring', checked: formData.termsAccepted.fairHiring, label: 'Fair-Hiring & Non-Discrimination Policy', desc: 'I agree to evaluate candidates solely on merit and skills.' },
                { name: 'terms.dataProcessing', checked: formData.termsAccepted.dataProcessing, label: 'Data Processing Agreement (GDPR/DPDP)', desc: 'I agree to securely handle student data according to privacy laws.' },
              ].map(term => (
                <label key={term.name} className="flex items-start gap-3 cursor-pointer group">
                  <input
                    name={term.name} type="checkbox" checked={term.checked} onChange={handleChange}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#1a3884] focus:ring-[#1a3884] cursor-pointer flex-shrink-0"
                  />
                  <div className="text-sm">
                    <span className="font-bold text-gray-700 group-hover:text-[#1a3884] transition-colors">{term.label}</span>
                    <p className="text-gray-500 text-xs mt-0.5">{term.desc}</p>
                  </div>
                </label>
              ))}
            </div>

            {/* Submit */}
            <button
              type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl text-sm font-bold text-white bg-[#1a3884] hover:bg-[#00152E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a3884] transition-all shadow-lg shadow-[#1a3884]/25 disabled:opacity-70"
            >
              {loading ? (
                <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting...</>
              ) : (
                <>Complete Registration <ArrowRight className="w-4 h-4" /></>
              )}
            </button>



          </form>
        </div>
      </div>
    </div>
  );
};

export default EmployerRegistration;
